/**
 * Copyright © Veeam Software Group GmbH.
 */

/**
 * Copyright © Veeam Software Group GmbH.
 */

import React from 'react';
import {
    CONTROL_SIZE,
    ErrorMessage,
    FormLayout,
    Notification,
    Spinner,
    STACK_ALIGN,
    STACK_DIRECTION,
    STACK_GAP,
    StackView,
    NOTIFICATION_STATUS,
    Icon,
    LoginBackIcon,
    INDENT,
} from '@veeam/components';

import { FormValidator, immediatelyValidateFlag, useExternalFormApi, Form, TextInput } from '../../../form';
import { useAutoFocus } from '../../../../hooks/use-auto-focus';
import { LOGIN_FORM_WIDTH } from '../FormContainer';
import { LoginTitle } from '../LoginTitle';
import { StyledLinkButton, LoginButton } from '../LoginButtons';
import { FieldLengths } from '../../../../enums';

import type { VerifyCodeFormState } from '../../interfaces';

const defaultTexts: Required<VerifyCodeFormProps['texts']> = {
    placeholderMFACode: 'Code',
    actionLogIn: 'Log in',
    returnButtonText: 'Back',
    errorRequired: 'The field is required',
    errorMaxLength: 'The maximum length is {0}',
    warningMFA: 'Your account uses two factor authentication. To login you need to enter the verification code from your device.',
    matchErrorMessage: 'Verification code format invalid',
};
const codeInputName = 'code';

export const verifyCodeFormInitialState: VerifyCodeFormState = {
    [codeInputName]: '',
};

export interface VerifyCodeFormProps {
    title?: string;
    onReturnButtonClick?: () => void;
    loading?: boolean;
    error?: string;
    disabled?: boolean;
    texts?: {
        placeholderMFACode?: string;
        actionLogIn?: string;
        returnButtonText?: string;
        errorRequired?: string;
        errorMaxLength?: string;
        warningMFA?: string;
        matchErrorMessage?: string;
    };
    logIn: (value: VerifyCodeFormState) => void;
    onChange?: (value: any) => void;
    customCodeValidation?: (value: string) => string | void;
    formWidth?: string;
    matchRegexp?: RegExp;
}

export const VerifyCodeForm: React.FC<VerifyCodeFormProps> = (props) => {
    const {
        title = 'Verification',
        texts: customTexts,
        loading,
        error,
        disabled,
        logIn,
        onReturnButtonClick,
        onChange,
        customCodeValidation,
        matchRegexp = new RegExp(`^[0-9]{${FieldLengths.MfaCode}}$`),
        formWidth = `${LOGIN_FORM_WIDTH}px`,
    } = props;
    const texts = { ...defaultTexts, ...customTexts };
    const codeInputRef = useAutoFocus();
    const formApi = useExternalFormApi<VerifyCodeFormState>();
    const stepValidate = (data: VerifyCodeFormState) => {
        const validator = new FormValidator(data);

        validator.validate(codeInputName)
            .string()
            .required(texts.errorRequired)
            .match(matchRegexp, `${texts.matchErrorMessage}${immediatelyValidateFlag}`)
            .check((value: string) => {
                if (customCodeValidation) {
                    return customCodeValidation(value);
                }
            });

        return validator.result();
    };

    return (
        <StackView direction={STACK_DIRECTION.column} gap={STACK_GAP.s} size={{ width: formWidth }}>
            {onReturnButtonClick && (
                <StyledLinkButton
                    compact
                    disabled={loading || disabled}
                    onClick={onReturnButtonClick}
                    iconBefore={<Icon src={<LoginBackIcon />} />}
                >
                    {texts.returnButtonText}
                </StyledLinkButton>
            )}

            <LoginTitle text={title} />

            <StackView direction={STACK_DIRECTION.column} gap={STACK_GAP.s} spaceTop={error ? INDENT.none : INDENT.s}>
                {error && <ErrorMessage>{error}</ErrorMessage>}
                <Notification status={NOTIFICATION_STATUS.info}>{texts.warningMFA}</Notification>

                <StackView direction={STACK_DIRECTION.column} gap={STACK_GAP.m}>
                    <Form
                        value={verifyCodeFormInitialState}
                        validate={stepValidate}
                        onChange={onChange}
                        externalFormApi={formApi}
                        onSubmit={logIn}
                        fields={[{ name: codeInputName, ref: codeInputRef }]}
                    >
                        <FormLayout disabled={loading || disabled}>
                            <TextInput
                                data-tid={`${VerifyCodeForm.name}_${codeInputName}`}
                                name={codeInputName}
                                controlRef={codeInputRef}
                                placeholder={texts.placeholderMFACode}
                                size={CONTROL_SIZE.full}
                            />
                        </FormLayout>
                    </Form>

                    <StackView
                        direction={STACK_DIRECTION.row}
                        gap={STACK_GAP.s}
                        align={STACK_ALIGN.center}
                    >
                        <LoginButton onClick={() => formApi.submit()} disabled={loading || disabled}>
                            {texts.actionLogIn}
                        </LoginButton>

                        {loading && <Spinner />}
                    </StackView>
                </StackView>
            </StackView>
        </StackView>
    );
};

